import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import BeatLoader from 'react-spinners/BeatLoader'

import ButtonTabNavbar from 'components/UI/TabNavbar/ButtonTabNavbar'
import { usePageTitle } from 'context/pageTitleContext'
import { usePreviewContext } from 'context/previewContext'
import { PostViewWrapper } from 'features/shared'
import {
  useFilterQuery,
  useInfiniteFeed
} from 'helpers/hooks'
import { useImpressionsTracker } from 'helpers/hooks/feed/useImpressionsTracker'
import WithoutHeaderSection from 'layouts/WithoutHeaderSection/WithoutHeaderSection'
import { getPurchasesFeed } from 'services/endpoints/media'
import { useUserProfile } from 'store'
import { usePurchasesCacheStore } from 'store/purchasesCacheStore'
import { type IPost } from 'types/interfaces/ITypes'

import styles from './purchases.module.scss'

type PurchaseFilter = 'photos' | 'videos'

export const Purchases = () => {
  const filter = useFilterQuery()

  const modelId = filter.get('model_id') ?? undefined
  const rawType = filter.get('type') ?? undefined

  const type: PurchaseFilter | undefined =
    rawType === 'photos' || rawType === 'videos'
      ? rawType
      : undefined
const forceFetchKey = useRef(Date.now()).current
  const userData = useUserProfile()!
  const { setTitle } = usePageTitle()
  const { addDebounceImpression } = useImpressionsTracker()

  const {
    setInitialData,
    selectedPostData,
    setSelectedPostData
  } = usePreviewContext()

const queryKey: [
  string,
  {
    filter?: PurchaseFilter
    model_id?: string
    force: number
  }
] = useMemo(
  () => [
    'getPurchasesHistory',
    {
      filter: type,
      model_id: modelId,
      force: forceFetchKey
    }
  ],
  [type, modelId, forceFetchKey]
)
  const cacheKey = useMemo(
    () =>
      [
        'purchases',
        type ?? 'all',
        modelId ?? 'all'
      ].join(':'),
    [type, modelId]
  )

  const cachedPosts = usePurchasesCacheStore(
    state => state.cache[cacheKey]?.posts
  )

  const setCachedPosts = usePurchasesCacheStore(
    state => state.setPosts
  )

  const [feedPosts, setFeedPosts] = useState<IPost[]>(
    cachedPosts ?? []
  )

  const {
    data,
    error,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    status,
    fetchNextPage
  } = useInfiniteFeed(
    queryKey,
    getPurchasesFeed
  )

  useEffect(() => {
    setTitle('Purchases')
  }, [setTitle])

  /*
   * When the filter changes, immediately hydrate the page
   * from the corresponding Zustand cache.
   */
  useEffect(() => {
    setFeedPosts(cachedPosts ?? [])
  }, [cacheKey, cachedPosts])

  /*
   * Fresh React Query data wins.
   *
   * Flatten all loaded infinite-query pages and store
   * the result in both local state and Zustand.
   */
  useEffect(() => {
    if (!data || isFetching) {
      return
    }

    const posts = data.pages.flatMap(
      page => page.page.data.data
    )

    setFeedPosts(posts)
    setCachedPosts(cacheKey, posts)
  }, [
    data,
    isFetching,
    cacheKey,
    setCachedPosts
  ])

  /*
   * Reinitialize preview data whenever the actual purchase
   * filter changes.
   */
  const previousPreviewKey = useRef<string | null>(null)

  useEffect(() => {
    if (!userData) {
      return
    }

    const previewKey =
      `${type ?? 'all'}:${modelId ?? 'all'}`

    if (previousPreviewKey.current === previewKey) {
      return
    }

    setInitialData(
      userData.id,
      type ?? 'all',
      'purchased',
      {},
      []
    )

    previousPreviewKey.current = previewKey
  }, [
    type,
    modelId,
    userData,
    setInitialData
  ])

  /*
   * Keep the selected preview post synced with the latest
   * version from the feed without writing the same object
   * back into context repeatedly.
   */
  useEffect(() => {
    if (!selectedPostData) {
      return
    }

    const selectedPost = feedPosts.find(
      post => post.id === selectedPostData.id
    )

    if (
      selectedPost &&
      selectedPost !== selectedPostData
    ) {
      setSelectedPostData(selectedPost)
    }
  }, [
    feedPosts,
    selectedPostData,
    setSelectedPostData
  ])

  const isMessagePurchase = (post: IPost) =>
    post.type === 'message'

  const observer = useRef<IntersectionObserver>()

  const lastPostRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) {
        return
      }

      observer.current?.disconnect()

      observer.current = new IntersectionObserver(
        entries => {
          if (
            entries[0]?.isIntersecting &&
            hasNextPage
          ) {
            fetchNextPage()
          }
        },
        {
          rootMargin: '1500px'
        }
      )

      if (node) {
        observer.current.observe(node)
      }
    },
    [
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage
    ]
  )

  useEffect(() => {
    return () => {
      observer.current?.disconnect()
    }
  }, [])

  const showInitialLoader =
    status === 'loading' &&
    feedPosts.length === 0

  if (status === 'error') {
    return (
      <WithoutHeaderSection customClass='purchases'>
        <div className='media__content__wrapper'>
          <div className={styles.wrapper}>
            <p>
              Error: {error?.message}
            </p>
          </div>
        </div>
      </WithoutHeaderSection>
    )
  }

  return (
    <WithoutHeaderSection customClass='purchases'>
      <div className='media__content__wrapper'>
        <div className={styles.wrapper}>
          <div className={styles.navigation}>
            <ButtonTabNavbar
              type='light'
              navArr={[
                'All',
                'photos',
                'videos'
              ]}
            />
          </div>

          <div className={styles.content}>
            {showInitialLoader ? (
              <div className={styles.loader}>
                <BeatLoader size={10} />
              </div>
            ) : (
              feedPosts.map(
                (
                  post: IPost,
                  index: number
                ) => {
                  const isLastPost =
                    index ===
                    feedPosts.length - 1

                  return (
                    <div
                      key={post.id}
                      ref={
                        isLastPost
                          ? lastPostRef
                          : undefined
                      }
                      style={{
                        paddingBottom: '12px',
                        boxSizing: 'border-box'
                      }}
                    >
                      <PostViewWrapper
                        post={post}
                        index={index}
                        addDebounceImpression={
                          addDebounceImpression
                        }
                        userData={userData}
                        userId={userData.id}
                        isLastPost={
                          isLastPost
                        }
                        dataQuery={
                          queryKey as any
                        }
                        hidePremiumBadge
                        hideComments={
                          isMessagePurchase(
                            post
                          )
                        }
                        hidePinned
                      />
                    </div>
                  )
                }
              )
            )}

            {isFetchingNextPage && (
              <div className={styles.loader}>
                <BeatLoader size={10} />
              </div>
            )}
          </div>
        </div>
      </div>
    </WithoutHeaderSection>
  )
}